/**
 * Stickyboard domain types — the wire shapes returned by messaging-service.
 *
 * Stickyboard has three domain objects: Section (swim-lane), Stack, and
 * StickyCard. A StickyCard references a Section and a Stack by id, both
 * OPTIONAL (a card may have neither). Sections and Stacks are per-user DTOs;
 * there is no fixed section set. Field names are snake_case to match the API
 * JSON verbatim (see messaging-service internal/model).
 */

export type StickySection = 'urgent' | 'today' | 'watching' | 'done' | (string & {}); // sections are free-form per-user ids; the four are common defaults

export type StickyStatus = 'active' | 'dismissed' | 'archived';

export type StickySenderKind = 'user' | 'agent';

export type SenderDisplayKind = 'user' | 'vau' | 'workflow_agent' | 'system_agent' | 'external';

export type ThreadRefKind = 'chat' | 'im';

export type CreatorKind = 'user' | 'steward';

export type SenderDisplay = {
  name: string;
  avatar_url?: string;
  kind: SenderDisplayKind;
};

export type ThreadRef = {
  kind: ThreadRefKind;
  thread_id: string;
};

/** A swim-lane on the board. Per-user; created lazily or by the user/Steward. */
export type Section = {
  section_id: string;
  instance_id: string;
  user_id: string;
  label: string;
  position: number;
  created_by: CreatorKind;
  created_at: string;
  updated_at: string;
};

/** A cluster of related cards. Independent of Section. Per-user. */
export type Stack = {
  stack_id: string;
  instance_id: string;
  user_id: string;
  label: string;
  created_by: CreatorKind;
  created_at: string;
  updated_at: string;
};

/** A single post-it. References a Section and a Stack by id, both optional. */
export type StickyCard = {
  sticky_id: string;
  instance_id: string;
  target_user_id: string;
  sender_id: string;
  sender_kind: StickySenderKind;
  sender_display: SenderDisplay;
  proteus_card?: Record<string, unknown>;
  title: string;
  body_excerpt: string;

  // Curator state — written by the Steward (optional FKs).
  curator_section_id?: string;
  curator_reason?: string;
  stack_id?: string;

  // User overrides — always win over curator state.
  user_section_id_override?: string;
  user_pinned: boolean;
  user_stack_override?: string;

  // The user's own state for an interactive proteus_card (e.g. checklist
  // toggle values keyed by jsonpointer path). Per-user UI state, persisted
  // so it survives refresh; does NOT re-trigger curation.
  user_card_data?: Record<string, unknown>;

  status: StickyStatus;
  dismissed_at?: string;
  archived_at?: string;

  linked_thread_ref?: ThreadRef;
  // The IM message that carries this sticky (set when the sticky is also an IM
  // message). With linked_thread_ref{kind:im} it deep-links the board → thread.
  linked_message_id?: string;
  source_event_ref?: string;

  created_at: string;
  updated_at: string;

  // Curation-freshness gate for auto-curate-on-visit.
  last_changed_at: string;
  last_curated_at?: string;
};

/**
 * effectiveSectionId / effectiveStackId mirror the server's resolution: the
 * user override wins over the curator's choice.
 */
export function effectiveSectionId(card: StickyCard): string | undefined {
  return card.user_section_id_override ?? card.curator_section_id;
}

export function effectiveStackId(card: StickyCard): string | undefined {
  return card.user_stack_override ?? card.stack_id;
}

/**
 * needsCuration mirrors the server predicate: an active card needs curation
 * when it has never been curated or was changed (by a human/agent) more
 * recently than it was last curated. Used to decide whether to fire the
 * Steward when the user opens Stickyboard.
 */
export function needsCuration(card: StickyCard): boolean {
  if (card.status !== 'active') return false;
  if (!card.last_curated_at) return true;
  return new Date(card.last_changed_at).getTime() > new Date(card.last_curated_at).getTime();
}

/** Whether ANY active card on the board needs curation (the board-level gate). */
export function boardNeedsCuration(cards: StickyCard[]): boolean {
  return cards.some(needsCuration);
}
